import {AfterViewInit, Component, ElementRef, HostListener, Input, OnDestroy, ViewChild} from '@angular/core';
import {filter} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Chart, ChartConfiguration, Color, Point, ScriptableScaleContext} from 'chart.js';
import annotationPlugin, {AnnotationOptions} from 'chartjs-plugin-annotation';
import zoomPlugin from 'chartjs-plugin-zoom';
import {MathUtils, Utils} from '@astek/utils';
import {RxUtils} from '../../../../shared/utils/rx-utils';
import {ConfigService} from '../../../../core/services/config/config.service';
import {PressureUnitEnum} from '../../../../core/services/state/pressure-unit.enum';
import {ThemeService} from '../../../../core/services/theme/theme.service';
import {InfusionInterval} from '../../services/data/infusion-interval';
import {PressureRange} from '../../services/data/pressure-range';
import {chartAreaStylePlugin} from '../../services/graph/chart-area-style.plugin';
import {PressureUnitUtils} from '../../../../core/utils/pressure-unit-utils';
import {GraphService} from '../../services/graph/graph.service';
import {SharedUtils} from '../../../../shared/utils/shared-utils';
import {commentsIndicatorsPlugin} from '../../services/graph/comments-indicators.plugin';
import {TimestampedComment} from '../../services/data/timestamped-comment';
import {MeasureTypeEnum} from '../../../../core/services/state/measure-type.enum';
import {ResultsConfig} from '../../../../core/services/config/results-config';
import {PressureScaleService} from '../../services/pressure-scale/pressure-scale.service';
import {PatientData} from '../../services/data/patient-data';
import {MeanPressureGraphConfig} from '../../../../core/services/config/mean-pressure-graph-config';
import {InfusionParametersResults} from '../../services/data/infusion-parameters-results';
import {BaselineParametersResults} from '../../services/data/baseline-parameters-results';

const ZOOM_PRECISION = 1e-2;

@Component({
  selector: 'xp-mean-pressure-graph',
  templateUrl: './mean-pressure-graph.component.html',
  styleUrls: [
    '../graph.common.scss',
    './mean-pressure-graph.component.scss',
  ],
  providers: [
    GraphService,
    PressureScaleService,
  ],
})
export class MeanPressureGraphComponent implements AfterViewInit, OnDestroy {

  @Input({required: true})
  public patientData!: PatientData;

  public readonly initialConfig: ChartConfiguration<'line', Array<Point>> = {
    type: 'line',
    data: {
      datasets: [
        {
          data: [],
          fill: false,
          pointStyle: false,
          spanGaps: true,
          cubicInterpolationMode: 'monotone',
        },
      ],
    },
    options: {
      // Turn off animations and data parsing for performance
      animation: false,
      parsing: false,
      normalized: true,
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'x',
      events: [],
      plugins: {
        legend: {display: false},
        annotation: {
          animations: {},
          annotations: [],
        },
        zoom: {
          limits: {},
          zoom: {
            mode: 'xy',
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },
            drag: {
              enabled: false,
            },
            onZoom: () => this.onZoom(),
          },
          pan: {
            enabled: true,
            mode: 'xy',
          },
        },
        chartAreaBorder: {},
        commentsIndicators: {},
        decimation: {
          enabled: true,
          algorithm: 'min-max',
        },
      },
    },
    plugins: [
      annotationPlugin,
      zoomPlugin,
      chartAreaStylePlugin,
      commentsIndicatorsPlugin,
    ],
  };

  @ViewChild('chartCanvas')
  public canvas!: ElementRef<HTMLCanvasElement>;
  public chart!: Chart<'line', Array<Point>>;

  @ViewChild('comment', {read: ElementRef})
  public commentRef!: ElementRef<HTMLElement>;

  private _readyToUpdate: boolean = false;

  private _maxTime: number = 0;

  private _commentIndicatorImage: HTMLImageElement = new Image();
  private _currentComment: TimestampedComment | null = null;


  constructor(
    private readonly _elementRef: ElementRef<HTMLElement>,
    private readonly _configService: ConfigService,
    private readonly _themeService: ThemeService,
    private readonly _graphService: GraphService,
    private readonly _pressureScaleService: PressureScaleService,
  ) {
    RxUtils.observeDoubleClick(this._elementRef.nativeElement).pipe(
      takeUntilDestroyed(),
      filter(() => this.isZoomedOrPanned),
    ).subscribe(() => {
      this.resetZoom();
    });
    this._themeService.darkThemeChanged.pipe(
      takeUntilDestroyed(),
      filter(() => Utils.isNotNullOrUndefined(this.chart)),
    ).subscribe(() => {
      this.chart.data.datasets[0].borderColor = this.graphConfig.curve.color.fromTheme(this.darkTheme);
      this.updateChartAreaStyle();
      this.updateTimeScale();
      this.updatePressureScale();
      this.updateAnnotations();
      this.update();
    });
    this.preloadCommentsImage();
  }

  public get unit(): PressureUnitEnum {
    return this.patientData.pressureUnit;
  }

  public get measureType(): MeasureTypeEnum {
    return this.patientData.measureType;
  }

  public get isInfusionTest(): boolean {
    return this.measureType === MeasureTypeEnum.INFUSION_TEST;
  }

  public get areRangesDisplayed(): boolean {
    return Utils.isNotNullOrUndefined(this.infusionResults)
      || Utils.isNotNullOrUndefined(this.baselineResults);
  }

  public get isZoomedOrPanned(): boolean {
    return this._readyToUpdate && (
      (this.chart?.isZoomedOrPanned() ?? false)
      || this.isXAxisZoomedOrPanned
      || this.isYAxisZoomedOrPanned
    );
  }

  public get isXAxisZoomedOrPanned(): boolean {
    return this._readyToUpdate && (
      !MathUtils.fuzzyEquals(
        (this.chart.scales['x'].options.min as number),
        0,
        ZOOM_PRECISION,
      ) || !MathUtils.fuzzyEquals(
        (this.chart.scales['x'].options.max as number),
        this._maxTime,
        ZOOM_PRECISION,
      )
    );
  }

  public get isYAxisZoomedOrPanned(): boolean {
    return this._readyToUpdate && (
      !MathUtils.fuzzyEquals(
        (this.chart.scales['y'].options.min as number),
        this._pressureScaleService.boundariesWithMargin.minPressure,
        ZOOM_PRECISION,
      ) || !MathUtils.fuzzyEquals(
        (this.chart.scales['y'].options.max as number),
        this._pressureScaleService.boundariesWithMargin.maxPressure,
        ZOOM_PRECISION,
      )
    );
  }

  public get xAxisZoomLevel(): number {
    if (!this._readyToUpdate) {
      return 1;
    }
    const currentMin = this.chart.scales['x'].options.min as number;
    const normalMin = 0;
    const currentMax = (this.chart.scales['x'].options.max as number);
    const normalMax = this._maxTime;
    return (normalMax - normalMin) / (currentMax - currentMin);
  }

  public get yAxisZoomLevel(): number {
    if (!this._readyToUpdate) {
      return 1;
    }
    const currentMin = this.chart.scales['y'].options.min as number;
    const normalMin = this._pressureScaleService.boundariesWithMargin.minPressure;
    const currentMax = (this.chart.scales['y'].options.max as number);
    const normalMax = this._pressureScaleService.boundariesWithMargin.maxPressure;
    return (normalMax - normalMin) / (currentMax - currentMin);
  }

  public get darkTheme(): boolean {
    return this._themeService.darkTheme;
  }

  public get isCommentVisible(): boolean {
    if (Utils.isNullOrUndefined(this._currentComment)
      || Utils.isNullOrUndefined(this.chart)) {
      return false;
    }
    return this.currentCommentPixelPosition > this.chart.scales['x'].left
      && this.currentCommentPixelPosition < this.chart.scales['x'].right;
  }

  public get currentCommentPixelPosition(): number {
    if (Utils.isNullOrUndefined(this._currentComment)
      || Utils.isNullOrUndefined(this.chart)) {
      return 0;
    }
    return this.chart.scales['x'].getPixelForValue(
      this._graphService.timestampToSeconds(this._currentComment.timestamp),
    );
  }

  public get commentLeftPosition(): number | string {
    if (Utils.isNullOrUndefined(this._currentComment)
      || Utils.isNullOrUndefined(this.chart)) {
      return 'unset';
    }
    if (this.commentOnRight) {
      // return this.chart.scales['x'].left;
      return this.chart.scales['x'].getPixelForValue(
        this._graphService.timestampToSeconds(this._currentComment.timestamp),
      );
    }
    return 'unset';
  }

  public get commentRightPosition(): number | string {
    if (Utils.isNullOrUndefined(this._currentComment)
      || Utils.isNullOrUndefined(this.chart)) {
      return 'unset';
    }
    if (!this.commentOnRight) {
      return this.chart.scales['x'].right - this.chart.scales['x'].getPixelForValue(
        this._graphService.timestampToSeconds(this._currentComment.timestamp),
      );
    }
    return 'unset';
  }

  public get commentOnRight(): boolean {
    if (Utils.isNullOrUndefined(this._currentComment)
      || Utils.isNullOrUndefined(this.chart)) {
      return true;
    }
    const pixelPos: number = this.chart.scales['x'].getPixelForValue(
      this._graphService.timestampToSeconds(this._currentComment.timestamp),
    );
    return this.chart.scales['x'].right - pixelPos > 0.5 * (this.chart.scales['x'].right - this.chart.scales['x'].left);
  }

  public get commentText(): string {
    return this._currentComment?.comment ?? '';
  }

  public get infusionIntervals(): Array<InfusionInterval> {
    return this.patientData.infusionIntervals;
  }

  public get chartAreaLeft(): number {
    if (Utils.isNullOrUndefined(this.chart)) {
      return 0;
    }
    return this.chart.scales['x'].left;
  }

  public get chartAreaRight(): number {
    if (Utils.isNullOrUndefined(this.chart)) {
      return 0;
    }
    return this.chart.width - this.chart.scales['x'].right;
  }

  public get chartAreaTop(): number {
    if (Utils.isNullOrUndefined(this.chart)) {
      return 0;
    }
    return this.chart.scales['y'].top;
  }

  public get chartAreaBottom(): number {
    if (Utils.isNullOrUndefined(this.chart)) {
      return 0;
    }
    return this.chart.height - this.chart.scales['y'].bottom;
  }

  public get graphConfig(): MeanPressureGraphConfig {
    return this._configService.config.measurements.meanPressureGraph;
  }

  private get infusionResults(): InfusionParametersResults | null {
    return this.patientData.results.infusionParametersResults;
  }

  private get baselineResults(): BaselineParametersResults | null {
    return this.patientData.results.baselineParametersResults;
  }

  private get zoomOutOffset(): number {
    return this._maxTime * (this.graphConfig.maxZoom.zoomOutMaxFactor - 1) / 2.0;
  }

  public ngAfterViewInit(): void {
    this._graphService.init(this.patientData);
    this._pressureScaleService.init(this.patientData, this.graphConfig);

    requestAnimationFrame(() => {
      // Use chart.js directly instead of ng2-charts to improve performance when the measure is updated in real time
      this.chart = new Chart(this.canvas.nativeElement, this.initialConfig);

      this.chart.data.datasets[0].borderColor = this.graphConfig.curve.color.fromTheme(this.darkTheme);
      this.chart.data.datasets[0].borderWidth = this.graphConfig.curve.thickness;
      this.updateChartAreaStyle();
      this.clearGraph();
      this.toggleZoomPan(true);
      this.updateCurve();
      if (Utils.isNotNullOrUndefined(this._graphService.lastMeasurement)) {
        this._maxTime = Math.max(
          this._maxTime,
          this._graphService.timestampToSeconds(this._graphService.lastMeasurement.timestamp),
        );
      }
      this.resetZoom();
      this.updateCommentsIndicators();

      this._readyToUpdate = true;
      this.update();
    });
  }

  public ngOnDestroy(): void {
    this._readyToUpdate = false;
    this.chart.destroy();
  }

  @HostListener('pointerdown', ['$event'])
  public onPointerDown(event: PointerEvent): void {
    this.handleCommentInteraction(event);
  }

  @HostListener('pointerup', ['$event'])
  public onPointerUp(event: PointerEvent): void {
    this.handleCommentInteraction(event);
  }

  @HostListener('pointermove', ['$event'])
  public onPointerMove(event: PointerEvent): void {
    this.handleCommentInteraction(event);
  }

  @HostListener('pointerenter', ['$event'])
  public onPointerEnter(event: PointerEvent): void {
    this.handleCommentInteraction(event);
  }

  @HostListener('pointerleave', ['$event'])
  public onPointerLeave(): void {
    this.hideComment();
  }

  @HostListener('pointercancel', ['$event'])
  public onPointerCancel(): void {
    this.hideComment();
  }

  public onZoom(): void {
    const data = this.chart.data.datasets[0].data;
    if (data.length > 0) {
      this.updateLiveTimeScale(data[data.length - 1].x);
    }
    this.updateZoomBehavior();
    this.updateAnnotations();
  }

  public handleResetZoom(event: MouseEvent): void {
    event.stopPropagation();
    this.resetZoom();
  }

  public resetZoom(): void {
    this.chart.resetZoom();
    this.chart.scales['x'].options.min = 0;
    this.chart.scales['x'].options.max = this._maxTime;

    const pressureBoundaries = this._pressureScaleService.boundariesWithMargin;
    this.chart.scales['y'].options.min = pressureBoundaries.minPressure;
    this.chart.scales['y'].options.max = pressureBoundaries.maxPressure;
    this.chart.options.plugins!.zoom!.limits!['y']!.min = pressureBoundaries.minPressure;
    this.chart.options.plugins!.zoom!.limits!['y']!.max = pressureBoundaries.maxPressure;

    this.updateAnnotations();
    this.update();
    this.updateZoomBehavior();
  }

  public update(): void {
    if (Utils.isNullOrUndefined(this.chart) || !this._readyToUpdate) {
      return;
    }
    this.chart.update();
  }

  public infusionIconPosition(interval: InfusionInterval): number | string {
    if (Utils.isNullOrUndefined(this.chart)) {
      return 'unset';
    }
    return Math.round(this.chart.scales['x'].getPixelForValue(
      this._graphService.timestampToSeconds(interval.beginTimestamp),
    ) - this.chartAreaLeft);
  }

  private updateChartAreaStyle(): void {
    this.chart.options.plugins!.chartAreaBorder = {
      backgroundColors: this.graphConfig.background.map(color => color.fromTheme(this.darkTheme)),
      borderColor: this.graphConfig.border.color.fromTheme(this.darkTheme),
      borderWidth: this.graphConfig.border.width,
      borderRadius: this.graphConfig.border.radius,
    };
  }

  private updateCommentsIndicators(): void {
    this.chart.options.plugins!.commentsIndicators = {
      timestamps: this.patientData.timestampedComments.map((val) => {
        return this._graphService.timestampToSeconds(val.timestamp);
      }),
      color: this.graphConfig.commentIndicator.color,
      imageHeight: this.graphConfig.commentIndicator.imageHeight,
      margin: this.graphConfig.commentIndicator.margin,
      image: this._commentIndicatorImage,
    };
  }

  private updateTimeScale(
    min: number = (this.chart.options.scales!['x']?.min ?? 0) as number,
    max: number = (this.chart.options.scales!['x']?.max ?? 0) as number,
  ): void {
    this._graphService.setupAxisScale(
      this.chart,
      this.graphConfig,
      'x',
      'bottom',
      min,
      max,
      this.graphConfig.maxZoom.time,
      this.graphConfig.graduations.timeStepSize.labels[0].value,
      'center',
      (tickValue: number | string): string | null => {
        if (Utils.isString(tickValue)) {
          return tickValue;
        }
        const tickOffset = tickValue - this.graphConfig.timeMinInterval.min;
        const minTime = this.chart.scales['x'].options.min as number;
        const maxTime = this.chart.scales['x'].options.max as number;
        const displayedRange = maxTime - minTime;
        const relativeThreshold = 0.04;
        // only display relevant timestamps
        if (
          MathUtils.fuzzyEquals(tickOffset, minTime, relativeThreshold * displayedRange)
          || MathUtils.fuzzyEquals(tickOffset, maxTime, relativeThreshold * displayedRange)
        ) {
          return '';
        }
        return SharedUtils.secondsToString(
          MathUtils.roundStep(tickOffset, this.graphConfig.graduations.timeStepSize.labels[0].value),
        );
      },
      (context: ScriptableScaleContext): Color => {
        const currentScaleRange = context.scale.max - context.scale.min;
        const currentTimeStep = this.graphConfig.graduations.timeStepSize.labels.find(
          (timeStep) => {
            return Utils.isNullOrUndefined(timeStep.maxRange) || (timeStep.maxRange > currentScaleRange);
          },
        );
        if (
          Utils.isNotNullOrUndefined(context.tick) && (Utils.isNotNullOrUndefined(currentTimeStep)
            && ((context.tick.value - this.graphConfig.timeMinInterval.min) % currentTimeStep.value !== 0))
        ) {
          return 'transparent';
        }
        let minTime = this.graphConfig.timeMinInterval.min;
        if (Utils.isNotNullOrUndefined(this._graphService.firstMeasurement)) {
          minTime = Math.min(
            minTime,
            this._graphService.timestampToSeconds(this._graphService.firstMeasurement.timestamp),
          );
        }
        let maxTime = this.graphConfig.timeMinInterval.max;
        if (Utils.isNotNullOrUndefined(this._graphService.lastMeasurement)) {
          maxTime = Math.max(
            maxTime,
            this._graphService.timestampToSeconds(this._graphService.lastMeasurement.timestamp),
          );
        }
        if (Utils.isNotNullOrUndefined(context.tick) && (
          (context.tick.value < minTime) || (context.tick.value > maxTime)
        )) {
          return this.graphConfig.graduations.outOfRangeColor.fromTheme(this.darkTheme);
        }
        return this.graphConfig.graduations.color.fromTheme(this.darkTheme);
      },
      (): Color => {
        return 'transparent';
      },
    );
  }

  private updatePressureScale(
    min: number = (this.chart.options.scales!['y']?.min ?? 0) as number,
    max: number = (this.chart.options.scales!['y']?.max ?? 0) as number,
  ): void {
    this._graphService.setupAxisScale(
      this.chart,
      this.graphConfig,
      'y',
      'left',
      min,
      max,
      this.graphConfig.maxZoom.pressure.fromUnit(this.unit),
      this._pressureScaleService.pressureLineStepSize,
      'center',
      null,
      (context: ScriptableScaleContext): Color => {
        if (
          Utils.isNotNullOrUndefined(context.tick)
          && (
            context.tick.value % this._pressureScaleService.pressureLabelStepSize !== 0
            || MathUtils.fuzzyEquals(
              context.tick.value,
              this._pressureScaleService.boundariesWithMargin.minPressure,
            )
            || MathUtils.fuzzyEquals(
              context.tick.value,
              this._pressureScaleService.boundariesWithMargin.maxPressure,
            )
          )
        ) {
          return 'transparent';
        }
        if (Utils.isNotNullOrUndefined(context.tick) && MathUtils.fuzzyEquals(
          context.tick.value,
          this.graphConfig.graduations.pressureThreshold.value.fromType(this.measureType).fromUnit(this.unit),
        )) {
          return this.graphConfig.graduations.pressureThreshold.color.fromTheme(this.darkTheme);
        }
        return this.graphConfig.graduations.color.fromTheme(this.darkTheme);
      },
      (context: ScriptableScaleContext): Color => {
        if (
          Utils.isNotNullOrUndefined(context.tick)
          && (
            context.tick.value % this._pressureScaleService.pressureLineStepSize !== 0
            || MathUtils.fuzzyEquals(
              context.tick.value,
              this._pressureScaleService.boundariesWithMargin.minPressure,
            )
            || MathUtils.fuzzyEquals(
              context.tick.value,
              this._pressureScaleService.boundariesWithMargin.maxPressure,
            )
          )
        ) {
          return 'transparent';
        }
        if (Utils.isNotNullOrUndefined(context.tick) && MathUtils.fuzzyEquals(
          context.tick.value,
          this.graphConfig.graduations.pressureThreshold.value.fromType(this.measureType).fromUnit(this.unit),
        )) {
          return this.graphConfig.graduations.pressureThreshold.color.fromTheme(this.darkTheme);
        }
        return this.graphConfig.graduations.lines.color.fromTheme(this.darkTheme);
      },
    );
  }

  /**
   * Stick to the right side either when zoomed out or when panning completely to the right side,
   * like when scrolling to the bottom of the console in JetBrains IDEs.
   *
   * @param currentTime Current time in seconds
   */
  private updateLiveTimeScale(currentTime: number): void {
    const maxTime = Math.max(this._maxTime, currentTime);
    const stickToRightSide = MathUtils.fuzzyEquals(this.chart.scales['x'].options.max as number, this._maxTime);

    if (!this.isZoomedOrPanned) {
      this.chart.scales['x'].options.min = 0;
    }
    if (!this.isZoomedOrPanned || stickToRightSide) {
      this.chart.scales['x'].options.max = maxTime;
    }

    if (!this.isZoomedOrPanned || this.chart.getZoomLevel() < 1) {
      this.chart.options.plugins!.zoom!.limits!['x']!.min = -this.zoomOutOffset;
      this.chart.options.plugins!.zoom!.limits!['x']!.max = maxTime + this.zoomOutOffset;
    } else {
      this.chart.options.plugins!.zoom!.limits!['x']!.min = 0;
      this.chart.options.plugins!.zoom!.limits!['x']!.max = maxTime;
    }

    this._maxTime = maxTime;
  }


  private updateZoomBehavior(): void {
    this.chart.options.plugins!.zoom!.pan!.enabled = !MathUtils.fuzzyEquals(this.xAxisZoomLevel, 1, ZOOM_PRECISION)
      || !MathUtils.fuzzyEquals(this.yAxisZoomLevel, 1, ZOOM_PRECISION);
    this.chart.options.plugins!.zoom!.zoom!.mode =
      (this.xAxisZoomLevel < (1 - ZOOM_PRECISION) && MathUtils.fuzzyEquals(this.yAxisZoomLevel, 1, ZOOM_PRECISION))
        ? 'x'
        : 'xy';
  }

  private updateAnnotations(): void {
    const annotations: Array<AnnotationOptions> = [];

    // Pressure ranges
    if (Utils.isNotNullOrUndefined(this.infusionResults)) {
      this.addPlateauAnnotations(annotations, this.infusionResults.basal, this.graphConfig.basalResults);
      this.addPlateauAnnotations(annotations, this.infusionResults.plateau, this.graphConfig.plateauResults);
    }
    if (Utils.isNotNullOrUndefined(this.baselineResults)) {
      this.addPlateauAnnotations(annotations, this.baselineResults.basal, this.graphConfig.basalResults);
    }

    // Infusion intervals
    annotations.push(
      ...this.patientData.infusionIntervals.map((interval: InfusionInterval): AnnotationOptions => {
        return {
          type: 'box',
          xMin: this._graphService.timestampToSeconds(interval.beginTimestamp),
          xMax: this._graphService.timestampToSeconds(interval.endTimestamp ?? interval.beginTimestamp),
          yMin: -Number.MAX_VALUE,
          yMax: Number.MAX_VALUE,
          borderWidth: this.graphConfig.infusion.border.width,
          backgroundColor: this.graphConfig.infusion.color.fromTheme(this.darkTheme),
          borderColor: this.graphConfig.infusion.border.color.fromTheme(this.darkTheme),
          drawTime: 'beforeDatasetsDraw',
        };
      }),
    );

    for (const comment of this.patientData.timestampedComments) {
      annotations.push(
        {
          type: 'line',
          xMin: this._graphService.timestampToSeconds(comment.timestamp),
          xMax: this._graphService.timestampToSeconds(comment.timestamp),
          yMin: -Number.MAX_VALUE,
          yMax: Number.MAX_VALUE,
          borderWidth: this.graphConfig.commentIndicator.lineWidth,
          borderColor: this.graphConfig.commentIndicator.color,
          drawTime: 'afterDatasetsDraw',
        },
      );
    }

    //!\\ the annotations must be set in a single assignment, pushing into the array will not work for some reason
    this.chart.options.plugins!.annotation!.annotations = annotations;
  }

  private addPlateauAnnotations(
    annotations: Array<AnnotationOptions>,
    range: PressureRange,
    resultsConfig: ResultsConfig,
  ): void {
    const {startInSeconds, endInSeconds} = this.computePressureRangeTime(range);
    const plateauPressure = PressureUnitUtils.convertMmHgToUnit(range.threshold, this.unit);
    annotations.push(
      // Plateau line
      {
        type: 'line',
        xMin: startInSeconds,
        xMax: endInSeconds,
        yMin: plateauPressure,
        yMax: plateauPressure,
        borderWidth: resultsConfig.line.width,
        borderColor: resultsConfig.line.color.fromTheme(this.darkTheme),
        drawTime: 'afterDatasetsDraw',
      },
    );
  }

  private clearGraph(): void {
    this._maxTime = Utils.isNotNullOrUndefined(this.patientData.lastMeasurement)
      ? this._graphService.timestampToSeconds(this.patientData.lastMeasurement.timestamp)
      : this.graphConfig.timeMinInterval.max;
    this.updateTimeScale(this.graphConfig.timeMinInterval.min, this._maxTime);
    this.resetPressureScale();
    this.chart.options.plugins!.annotation!.annotations = [];
    this.chart.data.datasets[0].data = [];
  }

  private resetPressureScale(): void {
    this._pressureScaleService.reset();
    this.updatePressureScale(
      this._pressureScaleService.boundariesWithMargin.minPressure,
      this._pressureScaleService.boundariesWithMargin.maxPressure,
    );
  }

  private updateCurve(): void {
    this.chart.data.datasets[0].data = this.patientData.measurements
      .filter(measurement => Utils.isNotNullOrUndefined(measurement.meanPressure))
      .map((measurement) => {
        this._pressureScaleService.updateBoundaryFromValue(measurement.meanPressure!);
        return {
          x: this._graphService.timestampToSeconds(measurement.timestamp),
          y: PressureUnitUtils.convertMmHgToUnit(measurement.meanPressure!, this.unit),
        };
      });
  }

  private computePressureRangeTime(range: PressureRange): { startInSeconds: number; endInSeconds: number } {
    const startInSeconds = this._graphService.timestampToSeconds(range.start);
    const endInSeconds = this._graphService.timestampToSeconds(range.end);
    return {startInSeconds, endInSeconds};
  }

  private toggleZoomPan(enabled: boolean): void {
    this.chart.options.plugins!.zoom!.zoom!.wheel!.enabled = enabled;
    this.chart.options.plugins!.zoom!.zoom!.pinch!.enabled = enabled;
    this.chart.options.plugins!.zoom!.pan!.enabled = enabled;
  }

  private handleCommentInteraction(event: PointerEvent): void {
    if (this.isCommentVisible) {
      const commentBox = this.commentRef.nativeElement.getBoundingClientRect();
      if (event.clientX > commentBox.left
        && event.clientX < commentBox.right
        && event.clientY > commentBox.top
        && event.clientY < commentBox.bottom) {
        return;
      }
      this.hideComment();
    }

    // find if a comment has been selected
    let selectedComment: TimestampedComment | null = null;
    let score: number = Number.MAX_VALUE;
    for (const comment of this.patientData.timestampedComments) {
      const commentXCoord = this.chart.scales['x'].getPixelForValue(
        this._graphService.timestampToSeconds(comment.timestamp),
      ) + this._elementRef.nativeElement.getBoundingClientRect().left;
      if (Utils.isNotNullOrUndefined(commentXCoord)) {
        const distanceToComment = Math.abs(event.clientX - commentXCoord);
        if (distanceToComment < this.graphConfig.commentIndicator.selectionDistanceThreshold
          && distanceToComment < score) {
          selectedComment = comment;
          score = distanceToComment;
        }
      }
    }
    if (Utils.isNotNullOrUndefined(selectedComment)) {
      console.info('Selected comment:', selectedComment);
      this.displayComment(selectedComment);
    }
  }

  private preloadCommentsImage(): void {
    this._commentIndicatorImage.src = 'assets/icons/comments.svg';
  }

  private displayComment(comment: TimestampedComment): void {
    this.hideComment();
    requestAnimationFrame(() => {
      this._currentComment = comment;
    });
  }

  private hideComment(): void {
    this._currentComment = null;
  }

}
